import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldProps, Form, Formik } from 'formik';
import { makeStyles } from '@material-ui/core/styles';

import {
  AuthenticationType,
  AuthAndClient,
  useGetAuthAndClientQuery,
  GetAuthAndClientQuery,
} from 'api/public';
import {
  ActionContainer,
  FormikEffect,
  Layouts,
  SubmitButton,
  ValidatedTextField,
} from 'components';
import { configureForClient } from 'config/Amplify';
import { useNotify } from 'hooks';
import { getSubdomainUrl } from 'helpers';
import * as paths from 'paths';
import storage from 'storage';

import { GenericSidebarHeader, Main } from '../components';
import { ClientRedirectSchema, IClientRedirectSchema } from '../validations';

const ES_AUTH_AND_CLIENT = 'esAuthAndClient';

const useStyles = makeStyles({
  '@global html, body, #root': {
    height: '100%',
  },
});

const ClientSelect: FC = () => {
  useStyles();

  const [emailAddress, setEmailAddress] = useState('');
  const notify = useNotify();
  const { t } = useTranslation();
  const userHasSpecificIntent =
    location.pathname !== '/' || (location.search && !location.search.includes('provider='));

  useEffect(() => {
    if (userHasSpecificIntent) {
      const authAndClient = JSON.parse(
        localStorage.getItem(ES_AUTH_AND_CLIENT) || 'null'
      ) as AuthAndClient;
      if (authAndClient) {
        navigateToClient(authAndClient);
      }
    }
  }, []);

  const { isLoading } = useGetAuthAndClientQuery(
    { email_address: emailAddress },
    {
      enabled: !!emailAddress,
      onSuccess: handleLookup,
      onError: notify.queryError,
    }
  );

  function handleLookup({ getAuthAndClient }: GetAuthAndClientQuery) {
    if (!getAuthAndClient) {
      return notify.error({ message: t('auth:generalError') });
    }

    if (!getAuthAndClient.authentication_type) {
      return notify.error({ message: t('auth:userNotFound') });
    }

    if (!getAuthAndClient.client) {
      return notify.error({ message: t('auth:clientNotFound') });
    }

    localStorage.setItem(ES_AUTH_AND_CLIENT, JSON.stringify(getAuthAndClient));
    navigateToClient(getAuthAndClient);
  }

  function onSubmit(values: IClientRedirectSchema) {
    setEmailAddress(values.username.toLowerCase());
  }

  function navigateToClient(getAuthAndClient: any) {
    const { authentication_type, existing_user, client } = getAuthAndClient;
    const { subdomain, sso_configuration } = client;

    if (sso_configuration) {
      configureForClient(client);
    }
    const subdomainUrl = getSubdomainUrl(subdomain);
    const destinationUrl = new URL(subdomainUrl);

    if (userHasSpecificIntent) {
      destinationUrl.pathname = location.pathname;
      destinationUrl.search = location.search;
    } else if (authentication_type === AuthenticationType.Auth) {
      destinationUrl.pathname = existing_user ? paths.login : paths.signUp;
    }

    window.location.assign(destinationUrl.href);
  }

  function handleChange({ username }: IClientRedirectSchema) {
    storage.userEmail = username;
  }

  function renderSidebar() {
    const initialValues: IClientRedirectSchema = { username: storage.userEmail };

    return (
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={ClientRedirectSchema}
      >
        <Form>
          <FormikEffect onChange={handleChange} delay={100} />
          <GenericSidebarHeader i18nKey="auth:signInExternal" />
          <ValidatedTextField
            name="username"
            i18nKey="user:email"
            onChange={(
              event: ChangeEvent<HTMLInputElement>,
              onChange: FieldProps['field']['onChange']
            ) => {
              event.target.value = event.target.value.trim();
              onChange(event);
            }}
          />
          <ActionContainer>
            <SubmitButton working={isLoading} text={t('auth:getStartedButton')} />
          </ActionContainer>
        </Form>
      </Formik>
    );
  }

  return (
    <Layouts.Full2Column
      sidebar={renderSidebar()}
      main={<Main headerI18nKey="auth:bodyHeader1" />}
    />
  );
};

export default ClientSelect;
